import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "train-dashboard-settings";

const DEFAULT_SETTINGS = {
  language: "ar",
  mapLayer: "transport",
  drawLines: true,
  refreshIntervalSeconds: 10,
  offlineThresholdHours: 48,
  hideTrainProgress: true,
  filters: {
    query: "",
    type: "all",
    selectedRouteIds: [],
    hideOffline: false,
  },
};

const deepMerge = (target, source) => {
  if (!source || typeof source !== "object") {
    return target;
  }

  const output = Array.isArray(target) ? [...target] : { ...target };

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = output[key];

    if (Array.isArray(sourceValue)) {
      output[key] = [...sourceValue];
    } else if (sourceValue && typeof sourceValue === "object") {
      output[key] = deepMerge(
        targetValue && typeof targetValue === "object" ? targetValue : {},
        sourceValue
      );
    } else {
      output[key] = sourceValue;
    }
  });

  return output;
};

const readStoredSettings = () => {
  if (typeof window === "undefined") {
    return DEFAULT_SETTINGS;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_SETTINGS;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return DEFAULT_SETTINGS;
    }

    return deepMerge(DEFAULT_SETTINGS, parsed);
  } catch (error) {
    console.warn("Failed to read stored settings", error);
    return DEFAULT_SETTINGS;
  }
};

const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  updateSettings: () => {},
  updateFilters: () => {},
  resetSettings: () => {},
});

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => readStoredSettings());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn("Failed to persist settings", error);
    }
  }, [settings]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const direction = settings.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = settings.language || "ar";
    document.body.dir = direction;
  }, [settings.language]);

  const updateSettings = useCallback((updater) => {
    setSettings((current) => {
      const next =
        typeof updater === "function" ? updater(current) : deepMerge(current, updater);
      return deepMerge(DEFAULT_SETTINGS, next);
    });
  }, []);

  const updateFilters = useCallback(
    (patch) => {
      updateSettings((current) => ({
        ...current,
        filters: {
          ...current.filters,
          ...patch,
        },
      }));
    },
    [updateSettings]
  );

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const value = useMemo(
    () => ({
      settings,
      updateSettings,
      updateFilters,
      resetSettings,
    }),
    [settings, updateFilters, updateSettings, resetSettings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

const useSettings = () => useContext(SettingsContext);

export { SettingsProvider, useSettings, DEFAULT_SETTINGS };
